import styled from '@emotion/styled';
import React, { forwardRef } from 'react';
import { Chip } from '../chip';
import { DropdownLabel, DropdownLabelProps } from './DropdownLabel';
import { Stack } from '../stack';
import { useProviderConfig } from '../provider';
import { buildCSSWithLength } from '../../styles';
import { css } from '@emotion/react';
import { input_chip_height_size_css } from '../chip/styles';
import { getDropdownChipLabelPaddingBySize } from './styles';

export interface DropdownChipLabelOption<OptionValue> {
  label: string;
  value: OptionValue;
  disabled?: boolean;
}

export interface DropdownChipLabelProps<OptionValue> extends Omit<DropdownLabelProps, 'onChange'> {
  allSelected?: boolean;
  selectedOptions: Array<DropdownChipLabelOption<OptionValue>>;
  onChange?: (values: Array<OptionValue>) => void;
}

export const DropdownChipLabel = forwardRef<HTMLDivElement, DropdownChipLabelProps<string | number>>(
  ({ disabled, size: sizeProp = 'medium', placeholder, selectedOptions, allSelected, onChange, ...props }, ref) => {
    const { locale } = useProviderConfig();
    const selected = selectedOptions.length > 0;
    const handleDeleteClick = (value: string | number) => {
      onChange?.(selectedOptions.filter((option) => option.value !== value).map(({ value }) => value));
    };

    return (
      <DropdownLabel
        ref={ref}
        disabled={disabled}
        size={sizeProp}
        selected={selected}
        actionCss={css`
          ${selected && input_chip_height_size_css[sizeProp]};
          align-items: center;
          position: relative;
          z-index: 2;
        `}
        {...(selected && {
          css: css`
            ${selected &&
            `
              ${buildCSSWithLength('padding', getDropdownChipLabelPaddingBySize(sizeProp))};
              ${buildCSSWithLength('align-items', allSelected ? 'center' : 'stretch')};
              height: auto;
              position: relative;
              z-index: 1;
            `};
          `,
        })}
        {...props}
      >
        {allSelected ? (
          locale?.DropdownFilter.all
        ) : selected ? (
          <ChipWrapper direction='row'>
            {selectedOptions.map(({ value, label }, index) => (
              <Chip key={index} size={sizeProp} disabled={disabled} onDelete={() => handleDeleteClick(value)}>
                {label}
              </Chip>
            ))}
          </ChipWrapper>
        ) : (
          placeholder ?? locale?.Dropdown.placeholder
        )}
      </DropdownLabel>
    );
  },
);

const ChipWrapper = styled(Stack)`
  gap: 4px;
  flex-wrap: wrap;
`;
